import { completeSignupBootstrap } from '@/src/contexts/signup-flow-state'
import { useResolvedAuthState } from '@/src/hooks/useResolvedAuthState'
import {
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query'

import { createUser } from '../../../dal/user-dal'
import { queryKeys } from '../../../queries/query-keys'
import {
  userQueryOptions,
  userWithRevenueCatIdQueryOptions,
} from '../../../queries/user-queries'
import { sendNewSubscriber } from '../../../services/mailchimp'
import {
  extractApiErrorMessage,
  extractStatusCode,
  parseApiError,
} from '../../../utils/parseApiError'
import { addBreadcrumb, captureException } from '../../../utils/sentry'

const MUTATION_KEY = queryKeys.mutations.createUser

class BackendUserStillMissingAfterCreateError extends Error {
  constructor() {
    super('Backend user is still missing after create-user recovery.')
    this.name = 'BackendUserStillMissingAfterCreateError'
  }
}

export interface CreateUserVariables {
  email: string | null
  firstName: string | null
}

export function isDuplicateUserError(err: unknown): boolean {
  const parsed = parseApiError(err)
  if (parsed?.status === 409) return true

  const combined = [
    parsed?.message ?? '',
    parsed?.bodyMessage ?? '',
    parsed?.bodyDetail ?? '',
    parsed?.bodyTitle ?? '',
    ...Object.values(parsed?.errors ?? {}).flat(),
    extractApiErrorMessage(err) ?? '',
    typeof err === 'string'
      ? err
      : err instanceof Error
        ? err.message
        : JSON.stringify(err ?? ''),
  ]
    .join(' ')
    .toLowerCase()

  return (
    combined.includes('duplicate key') ||
    combined.includes('ix_users_identity') ||
    combined.includes('cannot insert duplicate key')
  )
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient()
  const { authUserId, getAccessToken } = useResolvedAuthState()

  return useMutation({
    mutationKey: MUTATION_KEY,
    mutationFn: async ({ email, firstName }: CreateUserVariables) => {
      let createdBackendUser = null
      const accessToken = await getAccessToken()

      if (!accessToken) {
        throw new Error('Missing access token during user creation')
      }

      try {
        createdBackendUser = await createUser({ email, firstName })
        addBreadcrumb('setup', 'user_created')
      } catch (err: unknown) {
        if (!isDuplicateUserError(err)) throw err

        addBreadcrumb('setup', 'duplicate_user_handled', {
          statusCode: extractStatusCode(err),
        })
      }

      if (createdBackendUser != null) {
        return createdBackendUser
      }

      const resolvedUser = await queryClient.fetchQuery(
        userQueryOptions(authUserId, getAccessToken),
      )
      if (resolvedUser == null) {
        throw new BackendUserStillMissingAfterCreateError()
      }

      return resolvedUser
    },
    onSuccess: async (backendUser, { email, firstName }) => {
      queryClient.setQueryData(queryKeys.user.profile(authUserId), backendUser)

      if (email) {
        sendNewSubscriber(email, firstName ?? 'no-name')
      }

      if (
        backendUser.revenueCatId == null ||
        backendUser.revenueCatId.trim().length === 0
      ) {
        void queryClient
          .fetchQuery(userWithRevenueCatIdQueryOptions(authUserId))
          .then((resolvedUser) => {
            queryClient.setQueryData(
              queryKeys.user.profile(authUserId),
              resolvedUser,
            )
          })
          .catch(() => {
            addBreadcrumb('setup', 'revenuecat_id_pending_after_signup')
          })
      }

      await completeSignupBootstrap({
        authUserId,
        queryClient,
      })
    },
    onError: (err: unknown) => {
      const statusCode = extractStatusCode(err)
      captureException(err, {
        feature: 'setup',
        tags: { flow: 'create_user', statusCode: String(statusCode ?? '') },
      })
    },
  })
}

export type CreateUserStatus = 'idle' | 'pending' | 'success' | 'error'

export function useCreateUserStatus(): CreateUserStatus {
  const states = useMutationState({
    filters: { mutationKey: MUTATION_KEY },
    select: (mutation) => mutation.state.status,
  })

  const latest = states[states.length - 1]
  return latest ?? 'idle'
}
